import { wallLengthLockConflict } from './wallLengthLocks.js';
import { wallMountOffset } from './cameraMounts.js';
import { clamp, DEG, RAD, norm360, uid } from './constants.js';
import { polyArea } from './geometry.js';
import { findWallMount, validWallPose, wallAnchor } from './mounting.js';
import { sceneWalls } from './siteGeometry.js';
import { POLE_DEFAULTS } from './poles.js';
import { findPoleMount, poleCameraPose } from './poleMounting.js';
import { PERSON_DEFAULTS, FURNITURE_DEFAULTS } from './sceneObjects.js';
import { defaultLightHeight, exampleLight, guidedLightingScenario } from './lightingSetup.js';
import { formatLength } from './units.js';

export const objectKeys = { light: 'lights', cam: 'cams', wall: 'walls', area: 'areas', zone: 'zones', opening: 'openings', obstacle: 'obstacles', task: 'tasks', equipment: 'equipment', cable: 'cables', target: 'targets', stair: 'stairs' };
export const selectedObject = (S, sel) => sel?.type === 'bg' ? S.bg : S[objectKeys[sel?.type]]?.find(o => o.id === sel.id);

export function placePlanObject(store, kind, point) {
  const S = store.doc;
  const defaults = {
    light: { name: 'Light ' + ((S.lights || []).length + 1), definitionId: store.lightDefinitionId || S.lighting?.definitions[0]?.id || '', h: 3, pan: 0, tilt: 90, group: 'General', enabled: true, dimming: 1, maintenance: 1, poleId: '' },
    person: { ...PERSON_DEFAULTS, name: 'Person ' + (S.targets.length + 1) },
    ...Object.fromEntries(Object.entries(FURNITURE_DEFAULTS).map(([k, v]) => [k, { ...v, name: k[0].toUpperCase() + k.slice(1) + ' ' + (S.obstacles.filter(o => o.kind === k).length + 1), pan: 0, enabled: true, scenario: 'always' }])),
    equipment: { name: 'Switch ' + (S.equipment.length + 1), h: 1, ports: 8, poeBudgetW: 60, cost: 0 },
    obstacle: { name: 'Shelving ' + (S.obstacles.length + 1), kind: 'shelf', width: 2, depth: 0.5, height: 2, pan: 0, scenario: 'always', enabled: true },
    task: { name: 'Capture path ' + (S.tasks.length + 1), heading: 0, req: S.opt.profile === 'iec-2025' ? 'validate' : 'identify', width: 0.6, length: 1, height: S.opt.hFace, offset: 0, direction: 'forward', speed: 1.4, minDuration: 0.3, views: 1, maxYaw: S.opt.faceYaw ?? 45, maxDown: S.opt.aFace, enabled: true }
  };
  defaults.entrance = { ...defaults.task, name: 'Pedestrian entrance ' + (S.tasks.length + 1), width: 1.2, length: 3, minDuration: 0.5 };
  defaults.gate = { ...defaults.task, name: 'Vehicle gate ' + (S.tasks.length + 1), req: 'plate', width: 3, length: 8, height: S.opt.hPlate, speed: 2.8, minDuration: 0.5, targetWidth: 0.52, targetHeight: 0.11, maxYaw: S.opt.aPlateI, maxDown: S.opt.aPlateT };
  defaults.vehicle = { name: 'Parked vehicle ' + (S.obstacles.length + 1), kind: 'vehicle', width: 4.5, depth: 1.8, height: 1.5, pan: 0, scenario: 'always', enabled: true };
  for (const [k, v] of Object.entries(POLE_DEFAULTS)) defaults[k] = { ...v, name: (k === 'light-pole' ? 'Light pole ' : 'Utility pole ') + (S.obstacles.filter(o => o.kind === k).length + 1), pan: 0, enabled: true, scenario: 'always' };
  if (!defaults[kind] || !Number.isFinite(point.x) || !Number.isFinite(point.y)) return null;
  const type = kind === 'person' ? 'target' : ['entrance', 'gate'].includes(kind) ? 'task' : FURNITURE_DEFAULTS[kind] || POLE_DEFAULTS[kind] || kind === 'vehicle' ? 'obstacle' : kind;
  if (S[objectKeys[type]].length >= (type === 'target' ? 100 : type === 'obstacle' ? 500 : 200)) return null;
  const object = { id: uid(kind), ...defaults[kind], x: point.x, y: point.y };
  if (kind === 'light') object.h = defaultLightHeight(S, point);
  store.snapshot();
  if (kind === 'light') {
    // The toolbar and guided panel must both produce a usable light, including
    // after deleting a previously selected light type or undoing its import.
    if (!S.lighting.definitions.length) S.lighting.definitions.push(exampleLight(uid('luminaire')));
    object.definitionId = S.lighting.definitions.find(d => d.id === object.definitionId)?.id || S.lighting.definitions[0].id;
    if (!S.lighting.scenarios.length) {
      const scenario = guidedLightingScenario('night', uid('lightcase'), S.lighting.environment);
      S.lighting.scenarios.push(scenario); S.lighting.activeId = scenario.id;
    }
  }
  S[objectKeys[type]].push(object); store.sel = { type, id: object.id }; store.setTool('select'); store.touch();
  return object;
}

export function snapPlanPoint(S, p, previous, freeAngle = false, tolerance = 0.2) {
  let q = { x: p.x, y: p.y };
  if (S.opt.snap) {
    const candidates = [...S.walls.flatMap(w => [{ x: w.x1, y: w.y1 }, { x: w.x2, y: w.y2 }]), ...[...S.areas, ...S.zones].flatMap(a => a.pts)];
    const nearest = candidates.sort((a, b) => Math.hypot(a.x - p.x, a.y - p.y) - Math.hypot(b.x - p.x, b.y - p.y))[0];
    if (nearest && Math.hypot(nearest.x - p.x, nearest.y - p.y) < tolerance) return { ...nearest };
    const grid = Math.max(0.01, S.opt.grid);
    q = { x: Math.round(p.x / grid) * grid, y: Math.round(p.y / grid) * grid };
  }
  if (previous && S.opt.ortho && !freeAngle) {
    const dx = q.x - previous.x, dy = q.y - previous.y;
    const a = Math.round(Math.atan2(dy, dx) / (Math.PI / 4)) * Math.PI / 4;
    const length = dx * Math.cos(a) + dy * Math.sin(a);
    q = { x: previous.x + Math.cos(a) * length, y: previous.y + Math.sin(a) * length };
  }
  return q;
}

// Mount to the face actually hit by the pointer. Never search through a wall
// for a closer face on its far side, or use a door leaf / shelf as a support.
export function cameraOnSurface(S, wall, point, normal, camera = {}) {
  if (!wall || wall.objectKind || wall.mountRule === 'prohibited' || wall.kind !== 'solid' || Math.abs(normal.y) > 0.25) return null;
  const anchor = wallAnchor(wall, point.x, point.z);
  if (!anchor) return null;
  const length = Math.hypot(wall.x2 - wall.x1, wall.y2 - wall.y1);
  if (length < 0.4) return null;
  const ux = (wall.x2 - wall.x1) / length, uy = (wall.y2 - wall.y1) / length;
  const facing = -uy * normal.x + ux * normal.z;
  if (Math.abs(facing) < 0.75) return null; // End caps are not mounting faces.
  const side = facing >= 0 ? 1 : -1;
  const nx = -uy * side, ny = ux * side;
  const along = clamp((anchor.x - wall.x1) * ux + (anchor.y - wall.y1) * uy, 0.2, length - 0.2);
  const h = Math.round(point.y * 20) / 20;
  const offset = wallMountOffset(S.models?.find(m => m.id === camera.modelId), camera, wall);
  const pose = { ...camera, x: wall.x1 + ux * along + nx * offset, y: wall.y1 + uy * along + ny * offset, h, pan: norm360(Math.atan2(ny, nx) * DEG) };
  if (!(h >= 0.2) || !validWallPose(S, pose, wall)) return null;
  // Keep the old aim when it is still a valid view from this face.
  if (camera.pan != null && validWallPose(S, { ...pose, pan: camera.pan }, wall)) pose.pan = camera.pan;
  return pose;
}

export function aimCameraAt(S, camera, point) {
  const dx = point.x - camera.x, dy = point.z - camera.y;
  const distance = Math.hypot(dx, dy);
  if (distance < 0.05 && point.y >= camera.h - 0.05) return null;
  const pose = { ...camera, pan: distance < 0.05 ? camera.pan : norm360(Math.atan2(dy, dx) * DEG), tilt: clamp(Math.atan2(camera.h - point.y, distance) * DEG, 0, 90) };
  if (findPoleMount(S, camera) && !poleCameraPose(S, pose)) return null;
  const mount = findWallMount(S, camera);
  return mount && !validWallPose(S, pose, mount.wall) ? null : pose;
}

export function validPolygon(pts) {
  if (pts.length < 3 || pts.some(p => !Number.isFinite(p.x) || !Number.isFinite(p.y)) || polyArea(pts) < 0.01) return false;
  const cross = (a, b, c) => (b.x - a.x) * (c.y - a.y) - (b.y - a.y) * (c.x - a.x);
  for (let i = 0; i < pts.length; i++) {
    const a = pts[i], b = pts[(i + 1) % pts.length];
    if (Math.hypot(a.x - b.x, a.y - b.y) < 0.01) return false;
    for (let j = i + 2; j < pts.length; j++) {
      if (i === 0 && j === pts.length - 1) continue;
      const c = pts[j], d = pts[(j + 1) % pts.length];
      if (Math.max(a.x, b.x) + 1e-8 < Math.min(c.x, d.x) || Math.max(c.x, d.x) + 1e-8 < Math.min(a.x, b.x) || Math.max(a.y, b.y) + 1e-8 < Math.min(c.y, d.y) || Math.max(c.y, d.y) + 1e-8 < Math.min(a.y, b.y)) continue;
      if (cross(a, b, c) * cross(a, b, d) <= 0 && cross(c, d, a) * cross(c, d, b) <= 0) return false;
    }
  }
  return true;
}

export function finishPlanDraft(store, draft) {
  const S = store.doc, { kind, pts } = draft;
  if (kind === 'wall') {
    if (pts.length < 2 || pts.some((p, i) => i && Math.hypot(p.x - pts[i - 1].x, p.y - pts[i - 1].y) < 0.05)) return 'Draw at least one wall segment.';
    store.snapshot();
    for (let i = 1; i < pts.length; i++) {
      const wall = { id: uid('w'), x1: pts[i - 1].x, y1: pts[i - 1].y, x2: pts[i].x, y2: pts[i].y, kind: 'solid', h: draft.height || 3 };
      S.walls.push(wall); store.sel = { type: 'wall', id: wall.id };
    }
  } else if (kind === 'cable') {
    if (!S.cams.some(c => c.id === draft.camId) || !S.equipment.some(e => e.id === draft.equipmentId)) return 'Choose a camera and destination on the Install tab first.';
    store.snapshot();
    let route = S.cables.find(c => c.id === draft.routeId);
    if (route) route.pts = pts;
    else { route = { id: uid('r'), name: 'Cable ' + (S.cables.length + 1), camId: draft.camId, equipmentId: draft.equipmentId, height: 2.8, extra: 0, pts }; S.cables.push(route); }
    store.sel = { type: 'cable', id: route.id };
  } else {
    if (!validPolygon(pts)) return 'Draw a closed area with at least three corners and no crossing edges.';
    if (kind === 'building' && S.walls.length + pts.length > 4000) return 'This plan has reached the wall limit.';
    if (kind !== 'zone' && S.areas.length >= 200) return 'This plan has reached the area limit.';
    store.snapshot();
    if (kind === 'zone') {
      const zone = { id: uid('z'), name: 'Zone ' + (S.zones.length + 1), pts, req: S.opt.profile === 'iec-2025' ? 'validate' : 'identify', axis: 0, priority: 2 };
      S.zones.push(zone); store.sel = { type: 'zone', id: zone.id };
    } else {
      const exterior = ['outdoor', 'parking', 'road'].includes(kind), building = kind === 'building';
      const label = exterior ? kind === 'road' ? 'Road / driveway ' : kind === 'parking' ? 'Parking area ' : 'Exterior site ' : building ? 'Building interior ' : kind === 'floor' ? 'Area ' : 'Exclusion ';
      const area = { id: uid('a'), name: draft.name || label + (S.areas.length + 1), kind: kind === 'exclude' ? 'exclude' : 'floor', pts,
        environment: exterior ? 'outdoor' : 'indoor', ...(building ? { roofHeight: draft.height || 3 } : {}), purpose: building ? 'building' : ['parking', 'road'].includes(kind) ? kind : exterior ? 'yard' : '', surface: ['parking', 'road'].includes(kind) ? 'asphalt' : 'paving',
        req: exterior ? (S.opt.profile === 'iec-2025' ? 'discern' : 'observe') : building ? (S.opt.profile === 'iec-2025' ? 'perceive' : 'recognise') : '',
        ...(kind === 'road' ? { road: { heading: 0, lanes: 2, laneWidth: 3.2, direction: 'two-way', markings: true } } : {}),
        ...(kind === 'parking' ? { parking: { heading: 0, bayWidth: 2.5, bayDepth: 5, aisleWidth: 6 } } : {}) };
      S.areas.push(area); store.sel = { type: 'area', id: area.id };
      if (building) for (let i = 0; i < pts.length; i++) {
        const a = pts[i], b = pts[(i + 1) % pts.length];
        S.walls.push({ id: uid('w'), name: area.name + ' wall ' + (i + 1), x1: a.x, y1: a.y, x2: b.x, y2: b.y, kind: 'solid', h: draft.height || 3 });
      }
    }
  }
  store.touch(); return null;
}

export function openingAt(S, wall, point, kind = 'door', original = {}) {
  if (!wall || wall.objectKind || !S.walls.some(w => w.id === wall.id)) return null;
  const length = Math.hypot(wall.x2 - wall.x1, wall.y2 - wall.y1), width = original.width || (kind === 'window' ? 1.2 : 1);
  if (length < width + 0.1) return null;
  const along = clamp(((point.x - wall.x1) * (wall.x2 - wall.x1) + (point.y - wall.y1) * (wall.y2 - wall.y1)) / length, width / 2 + 0.05, length - width / 2 - 0.05);
  const bottom = kind === 'window' ? original.bottom ?? 0.9 : 0;
  const height = original.height ?? Math.min(kind === 'window' ? 1.2 : 2.1, wall.h - bottom - 0.1);
  if (height < 0.2 || bottom + height > wall.h) return null;
  if (S.openings.some(o => o.id !== original.id && o.wallId === wall.id && Math.abs(o.along - along) < (o.width + width) / 2 + 0.05)) return null;
  const opening = { ...original, wallId: wall.id, kind, along, width, height, bottom, openAngle: original.openAngle ?? 90, material: original.material || 'solid' };
  const next = { ...S, openings: [...S.openings.filter(o => o.id !== original.id), { ...opening, id: original.id || 'draft-opening' }] };
  if (S.cams.some(c => findWallMount(S, c) && !findWallMount(next, c))) return null;
  return opening;
}

// A wall edit carries its brackets and openings with it in one undo operation.
export function wallEdit(S, wall, patch) {
  const conflict = wallLengthLockConflict(S, wall, patch);
  if (conflict) return { error: conflict };
  const nextWall = { ...wall, ...patch }, oldL = Math.hypot(wall.x2 - wall.x1, wall.y2 - wall.y1), newL = Math.hypot(nextWall.x2 - nextWall.x1, nextWall.y2 - nextWall.y1);
  if (oldL < 0.01 || newL < 0.1) return { error: `Walls must be at least ${formatLength(.1, S)} long.` };
  const attached = S.cams.filter(c => { const m = findWallMount(S, c); return m && (m.wall.sourceId || m.wall.id) === wall.id; });
  if (attached.some(c => c.locked)) return { error: 'Unlock cameras mounted on this wall before moving it.' };
  const angle = Math.atan2(nextWall.y2 - nextWall.y1, nextWall.x2 - nextWall.x1) - Math.atan2(wall.y2 - wall.y1, wall.x2 - wall.x1);
  const cos = Math.cos(angle), sin = Math.sin(angle);
  const cams = attached.map(c => {
    const a = wallAnchor(wall, c.x, c.y), t = ((a.x - wall.x1) * (wall.x2 - wall.x1) + (a.y - wall.y1) * (wall.y2 - wall.y1)) / (oldL * oldL);
    const dx = c.x - a.x, dy = c.y - a.y;
    return { ...c, x: nextWall.x1 + t * (nextWall.x2 - nextWall.x1) + cos * dx - sin * dy, y: nextWall.y1 + t * (nextWall.y2 - nextWall.y1) + sin * dx + cos * dy, pan: norm360(c.pan + angle * DEG) };
  });
  const openings = S.openings.filter(o => o.wallId === wall.id).map(o => ({ ...o, along: o.along * newL / oldL }));
  if (openings.some(o => o.along - o.width / 2 < 0 || o.along + o.width / 2 > newL || (o.kind === 'window' ? o.bottom ?? 0.9 : 0) + o.height > nextWall.h || openings.some(p => p.id !== o.id && Math.abs(o.along - p.along) < (o.width + p.width) / 2))) return { error: 'This edit would squeeze an opening outside the wall or into another opening.' };
  const next = { ...S, walls: S.walls.map(w => w.id === wall.id ? nextWall : w), openings: S.openings.map(o => openings.find(p => p.id === o.id) || o) };
  if (cams.some(c => !sceneWalls(next).some(w => validWallPose(next, c, w)))) return { error: 'This edit would leave a camera without a valid wall mount.' };
  return { object: nextWall, cams, openings };
}
